/**
 * API Client
 *
 * HTTP client for communicating with the elBooks backend.
 * Supports both REST (via gRPC-Gateway) and handles timezone headers.
 */

import { parseError, handleNetworkError, parseResponseData } from './api-client-helpers';
import { getUserTimezone } from '../utils/timezone';

// ============================================================================
// Types
// ============================================================================

// Import and re-export shared types from api-types to avoid circular dependency
import type { ApiError, ApiResponse, ApiResult } from './api-types';
export type { ApiError, ApiResponse, ApiResult };

export interface ApiConfig {
  baseUrl: string;
  timeout?: number;
}

export interface RequestOptions {
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
}

export interface PageRequest {
  pageSize?: number;
  pageToken?: string;
}

export interface PageInfo {
  nextPageToken: string;
  hasNextPage: boolean;
  totalCount?: number;
  /**
   * Lower bound (or exact count, see remainingIsExact) of rows known to
   * exist after this page, from the backend's "peek 3 pages ahead" window.
   * Optional: not every endpoint has been wired to populate it yet.
   */
  remainingCount?: number;
  /** True when remainingCount is the exact remaining total; false => "many". */
  remainingIsExact?: boolean;
}

// ============================================================================
// Configuration
// ============================================================================

const DEFAULT_CONFIG: ApiConfig = {
  /* v8 ignore next */
  baseUrl: import.meta.env.VITE_API_URL ?? '/api/v1',
  timeout: 30000,
};

let config = { ...DEFAULT_CONFIG };

/**
 * Configure the API client.
 */
export function configureApi(newConfig: Partial<ApiConfig>): void {
  config = { ...config, ...newConfig };
}

// ============================================================================
// Request Helpers
// ============================================================================

/**
 * Convert camelCase to snake_case.
 */


/**
 * Build headers for API requests.
 * Includes timezone and locale for proper date/time handling.
 */
function buildHeaders(customHeaders?: Record<string, string>): Headers {
  return new Headers({
     
    'Content-Type': 'application/json',
    Accept: 'application/json',
     
    'X-Timezone': getUserTimezone(),
     
    /* v8 ignore next */
    'X-Locale': navigator.language ?? 'en-US',
    ...customHeaders,
  });
}

/**
 * Make an API request.
 */
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestOptions
): Promise<ApiResponse<T>> {
  const url = `${config.baseUrl}${path}`;
  const timeout = options?.timeout ?? config.timeout;

  const controller = new AbortController();
  /* v8 ignore next 3 */
  const timeoutId = timeout
    ? setTimeout(() => {controller.abort();}, timeout)
    : undefined;

  try {
    const response = await fetch(url, {
      method,
      headers: buildHeaders(options?.headers),
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.signal ?? controller.signal,
      credentials: 'include',
    });

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      const error = await parseError(response);
      return { error };
    }

    return parseResponseData<T>(response);
  } catch (err) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    return handleNetworkError(err);
  }
}

// ============================================================================
// HTTP Methods
// ============================================================================

export async function get<T>(
  path: string,
  options?: RequestOptions
): Promise<ApiResponse<T>> {
  return request<T>('GET', path, undefined, options);
}

export async function post<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions
): Promise<ApiResponse<T>> {
  return request<T>('POST', path, body, options);
}

export async function put<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions
): Promise<ApiResponse<T>> {
  return request<T>('PUT', path, body, options);
}

export async function patch<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions
): Promise<ApiResponse<T>> {
  return request<T>('PATCH', path, body, options);
}

export async function del<T>(
  path: string,
  options?: RequestOptions
): Promise<ApiResponse<T>> {
  return request<T>('DELETE', path, undefined, options);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Build query string from parameters.
 */
export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      if (typeof value === 'object') {
        searchParams.set(key, JSON.stringify(value));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string -- TODO(refactor): review and fix properly
        searchParams.set(key, String(value));
      }
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Check if response is successful.
 */
export function isSuccess<T>(response: ApiResponse<T>): response is { data: T } {
  return !response.error && response.data !== undefined;
}

/**
 * Check if response has error.
 */
export function isError<T>(response: ApiResponse<T>): response is { error: ApiError } {
  return !!response.error;
}
